import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  PROPERTY_TYPES,
  OPERATION_TYPES,
  ESTADO_OPTIONS,
  CURRENCIES,
} from "../utils/formatters";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { api } from "../services/apiClient";
import BackNavigationButton from "../components/navigation/BackNavigationButton";
import { useFormDraft } from "../hooks/useFormDraft";
import { useUnsavedChangesGuard } from "../hooks/useUnsavedChangesGuard";

const EditProperty = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [initialSnapshot, setInitialSnapshot] = useState("");
  const [myDevelopments, setMyDevelopments] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
    currency: "MXN",
    price_on_request: false,
    category: "Residencial",
    property_type: "",
    estado: "",
    operation_types: ["venta"],
    address: "",
    location_type: "",
    geometry: {
      type: "Point",
      coordinates: [-99.1332, 19.4326],
    },
    land_size_m2: "",
    construction_size_m2: "",
    bedrooms: "",
    bathrooms: "",
    half_bathrooms: "",
    parking: "",
    features: "",
    amenities: "",
    image_urls: "",
    cover_image_url: "",
    developer_id: "",
    development_id: "",
    allow_broker_representation: false,
    commission_percentage: 3.0,
    max_brokers: 3,
  });
  const draftKey = useMemo(() => `draft:edit-property:${id}`, [id]);
  const isDirty = useMemo(() => {
    if (loadingData || !initialSnapshot) return false;
    return JSON.stringify(formData) !== initialSnapshot;
  }, [formData, initialSnapshot, loadingData]);
  const { clearDraft } = useFormDraft({
    draftKey,
    formData,
    setFormData,
    enabled: !loadingData,
  });
  const { confirmIfDirty } = useUnsavedChangesGuard(isDirty);

  useEffect(() => {
    fetchProperty();
    fetchMyDevelopments();
  }, [id]);

  const fetchProperty = async () => {
    try {
      const response = await api.get(`/properties/${id}`);

      if (!response.ok) throw new Error("Propiedad no encontrada");

      const data = await response.json();

      const mappedFormData = {
        title: data.title || "",
        description: data.description || "",
        price: data.price_on_request
          ? ""
          : (data.price ?? data.price_mxn ?? data.price_usd ?? ""),
        currency:
          data.currency ||
          (data.price_mxn ? "MXN" : data.price_usd ? "USD" : "MXN"),
        price_on_request: !!data.price_on_request,
        category: data.category || "Residencial",
        property_type: data.property_type || data.subcategory || "",
        estado: data.estado || "",
        operation_types:
          data.operation_types && data.operation_types.length > 0
            ? data.operation_types
            : data.status === "venta_o_renta"
              ? ["venta", "renta"]
              : [data.status || "venta"],
        address: data.address || "",
        location_type: data.location_type || "",
        geometry: data.geometry || {
          type: "Point",
          coordinates: [-99.1332, 19.4326],
        },
        land_size_m2: data.land_size_m2 || "",
        construction_size_m2: data.construction_size_m2 || "",
        bedrooms: data.bedrooms || "",
        bathrooms: data.bathrooms || "",
        half_bathrooms: data.half_bathrooms || "",
        parking: data.parking || "",
        features: data.features?.join(", ") || "",
        amenities: data.amenities?.join(", ") || "",
        image_urls: data.image_urls?.slice(1).join(", ") || "",
        cover_image_url: data.image_urls?.[0] || "",
        developer_id: data.developer_id || "",
        development_id: data.development_id || "",
        allow_broker_representation: data.allow_broker_representation || false,
        commission_percentage: data.commission_percentage || 3.0,
        max_brokers: data.max_brokers || 3,
      };

      setFormData(mappedFormData);
      setInitialSnapshot(JSON.stringify(mappedFormData));

      setLoadingData(false);
    } catch (error) {
      toast.error("Error al cargar propiedad");
      navigate("/dashboard");
    }
  };

  const fetchMyDevelopments = async () => {
    try {
      const response = await api.get("/developments/my/all");
      if (response.ok) {
        const data = await response.json();
        setMyDevelopments(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error("Error fetching developments:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const propertyData = {
        ...formData,
        operation_types:
          formData.operation_types.length > 0
            ? formData.operation_types
            : ["venta"],
        status: formData.operation_types[0] || "venta",
        price:
          formData.price_on_request || !formData.price
            ? null
            : parseFloat(formData.price),
        currency: formData.currency || "MXN",
        price_on_request: !!formData.price_on_request,
        price_mxn:
          !formData.price_on_request &&
          formData.currency === "MXN" &&
          formData.price
            ? parseFloat(formData.price)
            : null,
        land_size_m2: formData.land_size_m2
          ? parseFloat(formData.land_size_m2)
          : null,
        construction_size_m2: formData.construction_size_m2
          ? parseFloat(formData.construction_size_m2)
          : null,
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
        bathrooms: formData.bathrooms ? parseInt(formData.bathrooms) : null,
        half_bathrooms: formData.half_bathrooms
          ? parseInt(formData.half_bathrooms)
          : null,
        parking: formData.parking ? parseInt(formData.parking) : null,
        features: formData.features
          ? formData.features.split(",").map((f) => f.trim())
          : [],
        amenities: formData.amenities
          ? formData.amenities.split(",").map((a) => a.trim())
          : [],
        image_urls: (() => {
          const urls = formData.image_urls
            ? formData.image_urls
                .split(",")
                .map((u) => u.trim())
                .filter((u) => u)
            : [];
          if (formData.cover_image_url && formData.cover_image_url.trim()) {
            const cover = formData.cover_image_url.trim();
            const filtered = urls.filter((u) => u !== cover);
            return [cover, ...filtered];
          }
          return urls;
        })(),
        commission_percentage: formData.allow_broker_representation
          ? parseFloat(formData.commission_percentage) || 3.0
          : null,
        max_brokers: formData.allow_broker_representation
          ? parseInt(formData.max_brokers) || 3
          : 3,
        development_id: formData.development_id || null,
      };

      const response = await api.put(`/properties/${id}`, propertyData);

      if (!response.ok) {
        let errorMessage = "Error al actualizar propiedad";
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          errorMessage = `Error ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      await response.json();
      toast.success("¡Propiedad actualizada exitosamente!");
      clearDraft();
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (!confirmIfDirty()) return;
    navigate("/dashboard");
  };

  if (loadingData) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted">
      <Header />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 md:py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="mb-6 md:mb-8">
            <div className="mb-4">
              <BackNavigationButton
                onBeforeNavigate={confirmIfDirty}
                dataTestId="edit-property-back"
              />
            </div>
            <h1
              className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 tracking-tight"
              style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
              data-testid="edit-property-title"
            >
              Editar Propiedad
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Actualiza la información de tu propiedad
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="space-y-6 md:space-y-8 bg-background rounded-lg p-6 md:p-8"
            data-testid="edit-property-form"
          >
            <div className="space-y-6">
              <h2
                className="text-xl md:text-2xl font-bold"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Información Básica
              </h2>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Título *
                </label>
                <Input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  data-testid="title-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Descripción *
                </label>
                <Textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={4}
                  data-testid="description-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Precio</label>
                <div className="mb-3 rounded-md border border-border/70 bg-muted/40 p-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.price_on_request}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          price_on_request: e.target.checked,
                          ...(e.target.checked ? { price: "" } : {}),
                        })
                      }
                      className="mt-1 h-4 w-4"
                      data-testid="price-on-request-checkbox"
                    />
                    <span>
                      <span className="font-medium text-sm block">
                        Publicar como "Precio a consultar"
                      </span>
                      <span className="text-xs text-muted-foreground">
                        El precio se comparte únicamente por contacto directo.
                      </span>
                    </span>
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium mb-2 block">
                      Precio
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) =>
                        setFormData({ ...formData, price: e.target.value })
                      }
                      placeholder={
                        formData.price_on_request
                          ? "Precio oculto al público"
                          : "0.00"
                      }
                      disabled={formData.price_on_request}
                      data-testid="price-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      Moneda
                    </label>
                    <Select
                      value={formData.currency}
                      onValueChange={(val) =>
                        setFormData({ ...formData, currency: val })
                      }
                    >
                      <SelectTrigger data-testid="currency-select">
                        <SelectValue placeholder="Moneda" />
                      </SelectTrigger>
                      <SelectContent>
                        {CURRENCIES.map((c) => (
                          <SelectItem key={c.value} value={c.value}>
                            {c.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <h2
                className="text-xl md:text-2xl font-bold"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Categorización
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Categoría *
                  </label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) =>
                      setFormData({
                        ...formData,
                        category: value,
                        property_type: "",
                      })
                    }
                  >
                    <SelectTrigger data-testid="category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Residencial">Residencial</SelectItem>
                      <SelectItem value="Comercial">Comercial</SelectItem>
                      <SelectItem value="Industrial">Industrial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Tipo de Propiedad *
                  </label>
                  <Select
                    value={formData.property_type}
                    onValueChange={(value) =>
                      setFormData({ ...formData, property_type: value })
                    }
                  >
                    <SelectTrigger data-testid="property-type-select">
                      <SelectValue placeholder="Selecciona..." />
                    </SelectTrigger>
                    <SelectContent>
                      {PROPERTY_TYPES[formData.category]?.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Estado *
                  </label>
                  <Select
                    value={formData.estado}
                    onValueChange={(value) =>
                      setFormData({ ...formData, estado: value })
                    }
                  >
                    <SelectTrigger data-testid="estado-select">
                      <SelectValue placeholder="Selecciona..." />
                    </SelectTrigger>
                    <SelectContent>
                      {ESTADO_OPTIONS.map((e) => (
                        <SelectItem key={e.value} value={e.value}>
                          {e.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Tipo de Operación *{" "}
                    <span className="text-muted-foreground font-normal">
                      (selecciona uno o más)
                    </span>
                  </label>
                  <div
                    className="flex flex-wrap gap-3"
                    data-testid="status-select"
                  >
                    {OPERATION_TYPES.map((op) => {
                      const checked = (formData.operation_types || []).includes(
                        op.value,
                      );
                      return (
                        <button
                          key={op.value}
                          type="button"
                          onClick={() => {
                            const current = formData.operation_types || [];
                            let next;
                            if (checked) {
                              next = current.filter((t) => t !== op.value);
                              if (next.length === 0) return;
                            } else {
                              next = [...current, op.value];
                            }
                            setFormData({ ...formData, operation_types: next });
                          }}
                          className={`px-4 py-2 rounded-full border-2 text-sm font-medium transition-all ${
                            checked
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-input bg-background hover:border-primary/60"
                          }`}
                        >
                          {op.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <h2
                className="text-xl md:text-2xl font-bold"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Ubicación
              </h2>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Dirección *
                </label>
                <AddressAutocomplete
                  value={formData.address}
                  onChange={(text) =>
                    setFormData({ ...formData, address: text })
                  }
                  onSelect={({ address, coordinates, estado }) => {
                    setFormData((prev) => ({
                      ...prev,
                      address,
                      geometry: { type: "Point", coordinates },
                      ...(estado && !prev.estado ? { estado } : {}),
                    }));
                  }}
                  placeholder="Buscar dirección con Mapbox..."
                  required
                  dataTestId="address-autocomplete-wrapper"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Tipo de Ubicación *
                </label>
                <Input
                  type="text"
                  value={formData.location_type}
                  onChange={(e) =>
                    setFormData({ ...formData, location_type: e.target.value })
                  }
                  data-testid="location-type-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Latitud (Norte/Sur)
                  </label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.geometry.coordinates[1]}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        geometry: {
                          ...formData.geometry,
                          coordinates: [
                            formData.geometry.coordinates[0],
                            parseFloat(e.target.value) || -100.3161,
                          ],
                        },
                      })
                    }
                    data-testid="latitude-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Longitud * (Oeste/Este, ej: -100.3161)
                  </label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.geometry.coordinates[0]}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        geometry: {
                          ...formData.geometry,
                          coordinates: [
                            parseFloat(e.target.value) || -100.3161,
                            formData.geometry.coordinates[1],
                          ],
                        },
                      })
                    }
                    data-testid="longitude-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Para Monterrey: -100 a -99 (negativo)
                  </p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                💡 <strong>Cómo obtener coordenadas:</strong> Abre Google Maps →
                busca tu dirección → click derecho en el punto exacto → click en
                las coordenadas que aparecen → se copian automáticamente.
              </p>
            </div>

            <div className="space-y-6">
              <h2
                className="text-xl md:text-2xl font-bold"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Características
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Tamaño Terreno (m²)
                  </label>
                  <Input
                    type="number"
                    value={formData.land_size_m2}
                    onChange={(e) =>
                      setFormData({ ...formData, land_size_m2: e.target.value })
                    }
                    data-testid="land-size-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Tamaño Construcción (m²)
                  </label>
                  <Input
                    type="number"
                    value={formData.construction_size_m2}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        construction_size_m2: e.target.value,
                      })
                    }
                    data-testid="construction-size-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Recámaras
                  </label>
                  <Input
                    type="number"
                    value={formData.bedrooms}
                    onChange={(e) =>
                      setFormData({ ...formData, bedrooms: e.target.value })
                    }
                    data-testid="bedrooms-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Baños Completos
                  </label>
                  <Input
                    type="number"
                    value={formData.bathrooms}
                    onChange={(e) =>
                      setFormData({ ...formData, bathrooms: e.target.value })
                    }
                    data-testid="bathrooms-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Medios Baños
                  </label>
                  <Input
                    type="number"
                    value={formData.half_bathrooms}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        half_bathrooms: e.target.value,
                      })
                    }
                    placeholder="0"
                    data-testid="half-bathrooms-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Estacionamientos
                  </label>
                  <Input
                    type="number"
                    value={formData.parking}
                    onChange={(e) =>
                      setFormData({ ...formData, parking: e.target.value })
                    }
                    data-testid="parking-input"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <h2
                className="text-xl md:text-2xl font-bold"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Detalles Adicionales
              </h2>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Características (separadas por comas)
                </label>
                <Textarea
                  value={formData.features}
                  onChange={(e) =>
                    setFormData({ ...formData, features: e.target.value })
                  }
                  placeholder="Ej: Piscina, Jardín, Terraza"
                  rows={3}
                  data-testid="features-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Amenidades (separadas por comas)
                </label>
                <Textarea
                  value={formData.amenities}
                  onChange={(e) =>
                    setFormData({ ...formData, amenities: e.target.value })
                  }
                  placeholder="Ej: Gym, Seguridad 24/7"
                  rows={3}
                  data-testid="amenities-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  🖼️ Imagen de Portada (URL)
                </label>
                <Input
                  type="url"
                  value={formData.cover_image_url}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      cover_image_url: e.target.value,
                    })
                  }
                  placeholder="https://ejemplo.com/portada.jpg"
                  data-testid="cover-image-url-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Esta será la imagen principal que se muestra en las listas
                </p>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  🎞️ URLs del Slideshow (separadas por comas)
                </label>
                <Textarea
                  value={formData.image_urls}
                  onChange={(e) =>
                    setFormData({ ...formData, image_urls: e.target.value })
                  }
                  placeholder="Ej: https://ejemplo.com/imagen1.jpg, https://ejemplo.com/imagen2.jpg"
                  rows={3}
                  data-testid="image-urls-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Estas imágenes se mostrarán en el carrusel de la página de
                  detalle
                </p>
              </div>
            </div>

            {/* Vincular a Desarrollo (opcional) */}
            {myDevelopments.length > 0 && (
              <div className="space-y-6">
                <h2
                  className="text-xl md:text-2xl font-bold"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  Vincular a un Desarrollo (Opcional)
                </h2>
                <div className="bg-secondary/5 border border-secondary/20 rounded-lg p-4">
                  <p className="text-sm text-muted-foreground mb-3">
                    Si esta propiedad forma parte de un desarrollo, selecciónalo
                    aquí.
                  </p>
                  <Select
                    value={formData.development_id || "none"}
                    onValueChange={(v) =>
                      setFormData({
                        ...formData,
                        development_id: v === "none" ? "" : v,
                      })
                    }
                  >
                    <SelectTrigger data-testid="development-select">
                      <SelectValue placeholder="Selecciona un desarrollo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin desarrollo</SelectItem>
                      {myDevelopments.map((dev) => (
                        <SelectItem key={dev.id} value={dev.id}>
                          {dev.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div className="space-y-6">
              <h2
                className="text-xl md:text-2xl font-bold"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Opciones de Representación por Brokers
              </h2>

              <div className="bg-accent/10 border border-accent/30 rounded-lg p-4">
                <div className="flex items-start space-x-3 mb-4">
                  <input
                    type="checkbox"
                    id="allow-broker-representation"
                    checked={formData.allow_broker_representation}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        allow_broker_representation: e.target.checked,
                      })
                    }
                    className="mt-1 h-4 w-4"
                    data-testid="allow-broker-checkbox"
                  />
                  <div className="flex-1">
                    <label
                      htmlFor="allow-broker-representation"
                      className="font-medium cursor-pointer"
                    >
                      Permitir representación por brokers (Co-listing)
                    </label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Los brokers certificados podrán solicitar representar esta
                      propiedad. Esto aumenta la visibilidad de tu propiedad y
                      las posibilidades de venta.
                    </p>
                  </div>
                </div>

                {formData.allow_broker_representation && (
                  <div className="mt-4 space-y-4 border-t border-accent/30 pt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium mb-2 block">
                          💰 Comisión Base (%)
                        </label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={formData.commission_percentage}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              commission_percentage:
                                parseFloat(e.target.value) || 3.0,
                            })
                          }
                          placeholder="3.0"
                          data-testid="commission-percentage-input"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Comisión inicial sugerida. Podrás negociarla con cada
                          broker al aprobar sus solicitudes.
                        </p>
                      </div>

                      <div>
                        <label className="text-sm font-medium mb-2 block">
                          👥 Máximo de Brokers
                        </label>
                        <Select
                          value={formData.max_brokers.toString()}
                          onValueChange={(value) =>
                            setFormData({
                              ...formData,
                              max_brokers: parseInt(value),
                            })
                          }
                        >
                          <SelectTrigger data-testid="max-brokers-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">
                              1 Broker (Exclusivo)
                            </SelectItem>
                            <SelectItem value="2">2 Brokers</SelectItem>
                            <SelectItem value="3">
                              3 Brokers (Recomendado)
                            </SelectItem>
                            <SelectItem value="4">4 Brokers</SelectItem>
                            <SelectItem value="5">5 Brokers</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">
                          Número máximo de brokers que pueden representar esta
                          propiedad simultáneamente.
                        </p>
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded p-3">
                      <p className="text-xs text-blue-800">
                        <strong>ℹ️ Nota sobre Co-listing:</strong> La comisión
                        se acuerda con cada broker al aprobar su solicitud y
                        queda bloqueada automáticamente para garantizar
                        seguridad. El broker que cierre la venta recibirá la
                        comisión acordada.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-end gap-3 md:gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                className="w-full sm:w-auto"
                data-testid="cancel-button"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto"
                data-testid="submit-button"
              >
                {loading ? "Guardando..." : "Guardar cambios"}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
};

export default EditProperty;
